(() => {
  'use strict';
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  class JewelryStandard extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.init().catch(error => {
        const root = this.shadowRoot || this;
        const message = root.querySelector('.availability') || this.querySelector('[data-loading]');
        if (message) message.textContent = 'Unable to load the latest product options. Please refresh or ask a Jewelry Expert.';
        root.querySelector('[data-add-to-cart]')?.setAttribute('disabled', '');
        const price = root.querySelector('.price');
        if (price) price.textContent = 'Price on request';
        console.error('Jewelry product could not be loaded', error);
      });
    }
    async init() {
      const data = JSON.parse(this.querySelector('[data-vr-jewelry-json]').textContent);
      const root = this.attachShadow({mode: 'open'});
      root.append(this.querySelector('[data-vr-jewelry-layout]').content.cloneNode(true));
      const seen = new Set();
      let next = data.next;
      while (next) {
        const url = new URL(next, location.href);
        if (url.origin !== location.origin || seen.has(url.href)) throw Error('Invalid variant pagination');
        seen.add(url.href);
        const current = new URL(location.href);
        for (const key of ['view', 'preview_theme_id']) if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
        const response = await fetch(url.href, {credentials: 'same-origin', cache: 'no-store'});
        if (!response.ok) throw Error('Variant page could not be loaded');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const page = JSON.parse(doc.querySelector('[data-vr-jewelry-json]')?.textContent || 'null');
        if (!page || page.id !== data.id) throw Error('Product changed during loading');
        data.variants.push(...page.variants);
        next = page.next;
      }
      data.variants = [...new Map(data.variants.map(v => [v.id, v])).values()];
      this.data = data;
      this.model = window.VRJewelryOptions.build(data, this.dataset.category);
      const initial = this.model.rows.find(r => r.variant.id === data.selectedVariantId)
        || this.model.rows.find(r => r.variant.available && r.variant.priceMinor > 0)
        || this.model.rows[0];
      this.selection = {...initial?.selection};
      const options = root.querySelector('.options');
      options.innerHTML = this.model.groups.map((group, i) => `<fieldset data-group="${escape(group.key)}"><legend>${escape(group.label)}</legend><div class="choices ${group.key === 'color' ? 'color-choices' : ''}">${group.values.map((value, j) => {
        const swatch = group.key === 'color' && ['White', 'Yellow', 'Rose'].includes(value);
        return `<label class="choice ${swatch ? 'swatch-choice' : ''}"><input type="radio" name="option-${i}" value="${escape(value)}" data-key="${escape(group.key)}" ${this.selection[group.key] === value ? 'checked' : ''}><span>${swatch ? `<i class="swatch-${value.toLowerCase()}" aria-hidden="true"></i><span class="sr-only">${escape(value)}</span>` : escape(value)}</span></label>`;
      }).join('')}</div></fieldset>`).join('');
      options.addEventListener('change', event => {
        const input = event.target;
        if (!input.matches('input[data-key]')) return;
        this.selection[input.dataset.key] = input.value;
        this.update();
      });
      root.querySelector('[data-add-to-cart]').addEventListener('click', () => this.addToCart());
      root.querySelectorAll('[data-show-media]').forEach(button => button.addEventListener('click', () => this.showMedia(button.dataset.showMedia)));
      const dialog = root.querySelector('dialog');
      root.querySelectorAll('.enlarge').forEach(button => button.addEventListener('click', () => {
        const source = button.querySelector('img');
        const image = dialog.querySelector('img');
        image.src = source.currentSrc || source.src;
        image.alt = source.alt;
        dialog.showModal();
      }));
      dialog.querySelector('.close').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
      root.querySelectorAll('details').forEach(detail => detail.addEventListener('toggle', () => { detail.querySelector('summary span').textContent = detail.open ? '−' : '+'; }));
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) { const back = root.querySelector('.back a'); const url = new URL(back.href); url.searchParams.set('preview_theme_id', preview); back.href = url.href; }
      this.update();
    }
    showMedia(id) {
      const root = this.shadowRoot;
      const items = [...root.querySelectorAll('[data-media-id]')];
      if (!items.some(item => item.dataset.mediaId === String(id))) return;
      items.forEach(item => { item.hidden = item.dataset.mediaId !== String(id); if (item.hidden) item.querySelectorAll('video').forEach(video => video.pause()); });
      root.querySelectorAll('[data-show-media]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.showMedia === String(id))));
    }
    update() {
      const state = window.VRJewelryOptions.resolve(this.model, this.selection, this.data);
      const root = this.shadowRoot;
      const price = root.querySelector('.price');
      price.textContent = state.quoted ? new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style: 'currency', currency: this.data.currency}).format(state.amount / 100)
        : !state.variant && !state.ambiguous ? 'Out of stock' : 'Price on request';
      const note = root.querySelector('.availability');
      const incomplete = Object.values(this.selection).some(v => /待确认|to be confirmed|unknown/i.test(v));
      note.textContent = !state.variant ? state.ambiguous ? 'Please ask a Jewelry Expert to confirm this configuration.' : 'Out of stock — this combination is not currently offered. Please ask a Jewelry Expert.'
        : !state.quoted ? 'No price is available for this selection. Please ask a Jewelry Expert.'
        : incomplete ? 'Please ask a Jewelry Expert to confirm these specifications.'
        : !this.data.purchaseEnabled ? '草稿 · 仅供预览，不开放购买'
        : !state.variant.available ? 'Out of stock — please ask a Jewelry Expert.' : '';
      const button = root.querySelector('[data-add-to-cart]');
      button.disabled = !state.purchasable || !!this.adding;
      button.textContent = this.adding ? 'Adding…' : !this.data.purchaseEnabled ? 'Preview only' : state.variant && state.quoted && state.variant.available ? 'Add to cart' : state.quoted || !state.variant ? 'Out of stock' : 'Price on request';
      if (state.variant?.mediaId) this.showMedia(state.variant.mediaId);
      root.querySelector('.cart-result').textContent = '';
    }
    async addToCart() {
      if (this.adding) return;
      const state = window.VRJewelryOptions.resolve(this.model, this.selection, this.data);
      if (!state.purchasable) return;
      this.adding = true;
      this.update();
      this.shadowRoot.querySelectorAll('.options input').forEach(input => { input.disabled = true; });
      let errorMessage = '';
      try {
        const response = await fetch(this.data.cartAddUrl, {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({items: [{id: state.variant.id, quantity: 1}]})});
        const result = await response.json();
        if (!response.ok || result.status) throw Error(typeof result.description === 'string' ? result.description : 'Unable to add this item. Please try again.');
      } catch (error) { errorMessage = error.message || 'Unable to add this item. Please try again.'; }
      finally { this.adding = false; this.shadowRoot.querySelectorAll('.options input').forEach(input => { input.disabled = false; }); this.update(); }
      const status = this.shadowRoot.querySelector('.cart-result');
      if (errorMessage) status.textContent = errorMessage;
      else { status.textContent = 'Added to cart. '; const link = document.createElement('a'); link.href = this.data.cartUrl; link.textContent = 'View cart'; status.append(link); }
    }
  }
  if (!customElements.get('vr-jewelry-standard')) customElements.define('vr-jewelry-standard', JewelryStandard);
})();
